import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, NO_COST_ORIGINS, isNoCostOrigin, type PhotoSlot } from "../lib/api";
import { CaptureSlot } from "../components/CaptureSlot";
import { Field } from "../components/Field";

/**
 * The store-aisle flow, in order: the record is created the moment you land
 * here, you shoot the four required frames, type the one number the camera
 * cannot know (what you paid), and the pipeline reads the rest off the photos.
 * The remaining listing/mannequin/measurement shots are added later from the
 * item's photo manager.
 */
const STEPS: Array<{ slot: PhotoSlot; caption: string; hint: string }> = [
  { slot: "thrift_tag", caption: "1 · Thrift Store Tag", hint: "The store tag — the listed price and category come from here." },
  { slot: "brand_tag", caption: "2 · Pad Print / Sewn Tag", hint: "The brand mark inside the garment — sets the brand and size." },
  { slot: "flat_front", caption: "3 · Garment — Flat — Front", hint: "The whole piece laid flat, front." },
  { slot: "flat_back", caption: "4 · Garment — Flat — Back", hint: "The whole piece laid flat, back." },
];

export function Intake() {
  const navigate = useNavigate();
  const [itemId, setItemId] = useState<string | null>(null);
  const [sku, setSku] = useState<string | null>(null);
  const [shot, setShot] = useState<Record<string, boolean>>({});
  const [purchasePrice, setPurchasePrice] = useState("");
  const [store, setStore] = useState("");
  const [stores, setStores] = useState<string[]>([]);
  // "store" = bought somewhere; otherwise one of the fixed no-cost origins
  // (own closet, gift), which means there is no purchase price to type.
  const [origin, setOrigin] = useState<string>("store");
  const [isPersonal, setIsPersonal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Create the record on arrival so photos have somewhere to upload.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const created = await api.post<{ id: string; sku: string }>("/items", {
          purchaseDate: new Date().toISOString().slice(0, 10),
        });
        if (cancelled) return;
        setItemId(created.id);
        setSku(created.sku);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Could not start a new item");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Stores already used, so the usual haunts are one tap rather than retyped
  // (and spelled the same every time, which the by-store breakdown depends on).
  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ sources: Array<{ name: string }> }>("/sources");
        setStores(r.sources.map((s) => s.name).filter((s) => !isNoCostOrigin(s)));
      } catch { /* free text still works */ }
    })();
  }, []);

  async function process() {
    if (!itemId) return;
    setBusy(true);
    setError(null);
    try {
      const patch: Record<string, unknown> = {};
      if (origin === "store") {
        const price = Number(purchasePrice);
        if (Number.isFinite(price) && purchasePrice !== "") patch.purchasePrice = price;
        if (store.trim()) patch.storeName = store.trim();
      } else {
        // From the family's closet or a gift: no cost basis, origin recorded
        // as the source so the by-store breakdown shows it at $0.
        patch.purchasePrice = 0;
        patch.storeName = origin;
      }
      if (isPersonal) patch.isPersonal = true;
      if (Object.keys(patch).length > 0) await api.patch(`/items/${itemId}`, patch);
      await api.post(`/items/${itemId}/enrich`);
      navigate(`/items/${itemId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not process this item");
      setBusy(false);
    }
  }

  if (!itemId) {
    return (
      <div className="shell">
        <header className="masthead"><span className="wordmark">new item</span></header>
        {error ? <div className="notice">{error}</div> : <p className="hint">Preparing a new item…</p>}
      </div>
    );
  }

  const shotCount = Object.values(shot).filter(Boolean).length;
  const originHint = NO_COST_ORIGINS.find((o) => o.name === origin)?.hint;

  return (
    <div className="shell">
      <header className="masthead">
        <span className="wordmark">new item</span>
        <span className="num" style={{ color: "var(--muted)" }}>{sku}</span>
      </header>

      {error && <div className="notice">{error}</div>}

      <section className="rail">
        <h2 className="eyebrow rail-label">Photograph the item ({shotCount}/4)</h2>
        <div className="slots">
          {STEPS.map((s) => (
            <CaptureSlot
              key={s.slot}
              itemId={itemId}
              slot={s.slot}
              caption={s.caption}
              onUploaded={() => setShot((prev) => ({ ...prev, [s.slot]: true }))}
            />
          ))}
        </div>
        <p className="hint">
          Shoot the store tag and brand mark in focus — the app reads the price, brand, size, and
          category straight off them. Front and back frames catalog the piece itself.
        </p>
      </section>

      <section className="rail">
        <h2 className="eyebrow rail-label">Where it came from</h2>
        <div className="origin-picker" role="radiogroup" aria-label="Where it came from">
          <button
            type="button"
            className={`origin-pick ${origin === "store" ? "active" : ""}`}
            aria-pressed={origin === "store"}
            onClick={() => setOrigin("store")}
          >
            Bought at a store
          </button>
          {NO_COST_ORIGINS.map((o) => (
            <button
              key={o.name}
              type="button"
              className={`origin-pick ${origin === o.name ? "active" : ""}`}
              aria-pressed={origin === o.name}
              title={o.hint}
              onClick={() => setOrigin(o.name)}
            >
              {o.label}
            </button>
          ))}
        </div>

        {origin === "store" ? (
          <div className="row">
            <Field label="Purchase price" hint="After any color-tag discount">
              <input
                className="money"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
              />
            </Field>
            <Field label="Store" hint="Feeds the by-store breakdown">
              <input
                type="text"
                list="intake-stores"
                placeholder="e.g. Goodwill"
                value={store}
                onChange={(e) => setStore(e.target.value)}
              />
              <datalist id="intake-stores">
                {stores.map((s) => <option key={s} value={s} />)}
              </datalist>
            </Field>
          </div>
        ) : (
          <p className="hint" style={{ marginTop: 0 }}>
            {originHint}. Purchase price is recorded as $0.00, so the whole sale (less fees) is profit.
          </p>
        )}

        <label className="toggle-row">
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={isPersonal}
            onChange={(e) => setIsPersonal(e.target.checked)}
          />
          <span>
            Keeping it — not for resale
            <span className="hint" style={{ margin: 0 }}>
              Still tracked, but kept out of capital spent, inventory value and profit.
            </span>
          </span>
        </label>
      </section>

      <div className="actions">
        <button onClick={() => void process()} disabled={busy || shotCount === 0}>
          {busy ? "Processing…" : "Process item"}
        </button>
        <button className="quiet" onClick={() => navigate("/")}>Finish later</button>
      </div>

      {shotCount === 0 && <p className="hint">Take at least the store-tag photo before processing.</p>}
    </div>
  );
}
